import * as React from 'react';
import { DirectionalHint, IContextualMenuItem, IContextualMenuProps, Selection } from '@fluentui/react';

type AnchorPoint = { x: number; y: number };

export type UseDetailsListContextMenuResult = {
    contextMenuProps: IContextualMenuProps | undefined;
    onItemContextMenu: (item?: unknown, index?: number, ev?: Event) => boolean;
};

/**
 * Wires a Fluent UI DetailsList row right-click to a ContextualMenu anchored at the
 * cursor. Mirrors the desktop convention: right-clicking a row outside the current
 * selection replaces the selection with just that row; right-clicking a row already
 * in a multi-selection preserves the selection so bulk actions still apply.
 *
 * Consumers spread the returned `onItemContextMenu` onto DetailsList and render
 * `<ContextualMenu {...contextMenuProps} />` when defined.
 */
export function useDetailsListContextMenu(
    selection: Selection | null,
    items: IContextualMenuItem[]
): UseDetailsListContextMenuResult {
    const [anchor, setAnchor] = React.useState<AnchorPoint | null>(null);

    const onItemContextMenu = React.useCallback((_item?: unknown, index?: number, ev?: Event): boolean => {
        const mouseEvent = ev as MouseEvent | undefined;
        if (!mouseEvent || !selection) return false;

        mouseEvent.preventDefault();

        if (typeof index === 'number') {
            const currentIndices = selection.getSelectedIndices();
            if (!currentIndices.includes(index)) {
                // Coalesce the clear + select into a single onSelectionChanged fire so consumers
                // never see a transient empty-selection state. The second arg of the final
                // setChangeEvents call MUST be omitted (or false) — passing true would suppress
                // the buffered change entirely, leaving the parent state stale on first open.
                selection.setChangeEvents(false);
                selection.setAllSelected(false);
                selection.setIndexSelected(index, true, true);
                selection.setChangeEvents(true);
            }
        }

        setAnchor({ x: mouseEvent.clientX, y: mouseEvent.clientY });
        return false;
    }, [selection]);

    const dismiss = React.useCallback(() => setAnchor(null), []);

    // If the items list collapses to empty while the menu is open (e.g. the right-clicked
    // row was removed by a refresh / SignalR push, clearing selectedItems upstream), close
    // the menu instead of rendering an empty Callout the user can't easily dismiss.
    React.useEffect(() => {
        if (anchor && items.length === 0) setAnchor(null);
    }, [anchor, items]);

    const contextMenuProps = React.useMemo<IContextualMenuProps | undefined>(() => {
        if (!anchor || items.length === 0) return undefined;
        return {
            target: { left: anchor.x, top: anchor.y, right: anchor.x, bottom: anchor.y },
            items,
            onDismiss: dismiss,
            // bottomLeftEdge anchors the menu's LEFT edge to the cursor so it grows
            // rightward (OS desktop convention). Callout still auto-flips when the
            // cursor is near the right edge of the viewport.
            directionalHint: DirectionalHint.bottomLeftEdge,
        };
    }, [anchor, items, dismiss]);

    return { contextMenuProps, onItemContextMenu };
}
